import React, { useState, useEffect, useRef, useMemo } from "react";
import GlobalNav from "../components/GlobalNav";
import Footer from "../components/Footer";
import "./MusicPage.css";

/*
  Backed by GET /api/releases/map, which queries the release_locations
  table (see migrations/005_release_locations.sql + 006_..._seed.sql) —
  17 real releases parsed directly from releases.csv. PLACEHOLDER_RELEASES
  below is only a last-resort fallback if that fetch fails (network error,
  server down), not the primary data source anymore.
*/
const PLACEHOLDER_RELEASES = [
  {
    location: "Cambridge, MA", lat: 42.3736, lon: -71.1097,
    artist: "Akwerius", song: "Love Rule", year: "2026",
    url: "/images/slider/liveinstudio.jpg",
    audio: "https://wordsoundlife-berkeley-edu.apphost.ocf.berkeley.edu/assets/akwerius/LoveRule.mp3",
    video: null,
    producer: "Circadian Riddims", releaseDate: "2026", runningTime: "4:12",
  },
];

const MAP_WIDTH = 1024;
const MAP_HEIGHT = 512;
// Full-page photo backdrop (Mt Shasta night sky) — same real asset used on
// the EPK hero.
const MAP_BG_IMAGE = "/images/mtshasta.jpg";
// Continent outlines. Bubble positions use a real Web Mercator projection
// (mercX/mercY below, zoom=1) — the ONLY reason continents were visible in
// the original map.js is that it used an actual Mapbox raster tile, which
// is Web Mercator too, so bubbles landed exactly on real coastlines. A
// generic world-map image wouldn't line up (equirectangular vs Mercator
// distort high latitudes very differently). Bringing this back as a
// semi-transparent overlay, not the sole background, keeps bubbles
// correctly placed while the photo shows through underneath.
const MAP_OUTLINE_IMAGE =
  "https://api.mapbox.com/styles/v1/mapbox/dark-v9/static/0,0,1,0,0/1024x512?access_token=pk.eyJ1IjoibXVzaWNpc2hlYWxpbmd1cyIsImEiOiJjamhnZWRvdHkxazB1M2RvNmx4cTZxZTZ5In0.ahLgYEuVqFQ9oq1iwfwM9g";

// Mercator projection — ported directly from map.js's mercX/mercY (same
// math, just Math.* instead of p5's global radians/pow/tan/log).
function mercX(lon, zoom = 1) {
  const rad = (lon * Math.PI) / 180;
  const a = (256 / Math.PI) * Math.pow(2, zoom);
  return a * (rad + Math.PI);
}

function mercY(lat, zoom = 1) {
  const rad = (lat * Math.PI) / 180;
  const a = (256 / Math.PI) * Math.pow(2, zoom);
  const b = Math.tan(Math.PI / 4 + rad / 2);
  return a * (Math.PI - Math.log(b));
}

// Fixed map center used by the original sketch (clat=0, clon=0).
const CENTER_X = mercX(0);
const CENTER_Y = mercY(0);

export default function MusicPage() {
  const [releases, setReleases] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [hoveredRelease, setHoveredRelease] = useState(null);
  const [videoModal, setVideoModal] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [zoom, setZoom] = useState(1);
  const audioRef = useRef(null);

  useEffect(() => {
    fetch("/api/releases/map")
      .then((r) => r.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : data.releases;
        setReleases(Array.isArray(list) && list.length ? list : PLACEHOLDER_RELEASES);
      })
      .catch(() => setReleases(PLACEHOLDER_RELEASES));
  }, []);

  const allReleases = releases ?? [];

  const years = useMemo(() => {
    const set = new Set(allReleases.map((r) => r.year).filter(Boolean));
    return Array.from(set).sort().reverse();
  }, [allReleases]);

  const yearCounts = useMemo(() => {
    const counts = {};
    allReleases.forEach((r) => { if (r.year) counts[r.year] = (counts[r.year] || 0) + 1; });
    return counts;
  }, [allReleases]);

  const locations = useMemo(() => {
    const set = new Set(allReleases.map((r) => r.location).filter(Boolean));
    return Array.from(set).sort();
  }, [allReleases]);

  const locationCounts = useMemo(() => {
    const counts = {};
    allReleases.forEach((r) => { if (r.location) counts[r.location] = (counts[r.location] || 0) + 1; });
    return counts;
  }, [allReleases]);

  const filteredReleases = useMemo(() => {
    return allReleases.filter((r) => {
      if (selectedYear && r.year !== selectedYear) return false;
      if (selectedLocation && r.location !== selectedLocation) return false;
      return true;
    });
  }, [allReleases, selectedYear, selectedLocation]);

  function bubblePosition(release) {
    const x = mercX(release.lon) - CENTER_X + MAP_WIDTH / 2;
    const y = mercY(release.lat) - CENTER_Y + MAP_HEIGHT / 2;
    return {
      left: `${(x / MAP_WIDTH) * 100}%`,
      top: `${(y / MAP_HEIGHT) * 100}%`,
    };
  }

  function handleReleaseClick(release, index) {
    if (release.video) {
      setVideoModal(release);
    } else if (release.audio) {
      playTrack(release, index);
    }
  }

  function playTrack(release, index) {
    if (!audioRef.current) return;
    if (playingId === index && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }
    audioRef.current.src = release.audio;
    audioRef.current.play();
    setPlayingId(index);
    setIsPlaying(true);
  }

  function togglePlay() {
    if (!audioRef.current || playingId === null) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  }

  function seek(seconds) {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime + seconds);
  }

  return (
    <div className="music-page">
      <img className="music-page-bg" src={MAP_BG_IMAGE} alt="" aria-hidden="true" />
      <GlobalNav activeHref="/music" ctaLabel="Join" />

      {/* <div className="music-hero">
        <div className="music-hero-inner">
          <div className="music-hero-eyebrow">Explore the map</div>
          <h1 className="music-hero-title">Music</h1>
          <p className="music-hero-sub">Releases plotted by where they were made</p>
        </div>
      </div> */}

      <div className="music-body">
        <div className="map-layout">

          {/* Sidebar: years */}
          <div className="map-sidebar map-sidebar--years">
            <div className="map-sidebar-label">Years</div>
            <button
              className={"map-filter-link" + (!selectedYear ? " active" : "")}
              onClick={() => setSelectedYear(null)}
            >
              All years
            </button>
            {years.map((y) => (
              <button
                key={y}
                className={"map-filter-link" + (selectedYear === y ? " active" : "")}
                onClick={() => setSelectedYear(selectedYear === y ? null : y)}
              >
                {y} <span className="map-filter-count">({yearCounts[y]})</span>
              </button>
            ))}
          </div>

          {/* Map + content grid */}
          <div className="map-center">
            <div className="map-now-showing">
              <span>{selectedYear || "All years"} in {selectedLocation || "all locations"}</span>
              <span>{filteredReleases.length} result{filteredReleases.length === 1 ? "" : "s"}</span>
            </div>

            <div className="map-canvas">
              <img className="map-outline" src={MAP_OUTLINE_IMAGE} alt="" aria-hidden="true" />
              <div className="map-zoom-controls">
                <button onClick={() => setZoom((z) => Math.min(2.5, z + 0.25))} aria-label="Zoom in">+</button>
                <button onClick={() => setZoom((z) => Math.max(1, z - 0.25))} aria-label="Zoom out">−</button>
              </div>
              <div className="map-bubble-layer" style={{ transform: `scale(${zoom})` }}>
                {filteredReleases.map((r, i) => (
                  <button
                    key={`${r.artist}-${r.song}-${i}`}
                    className={"map-bubble" + (r.video ? " map-bubble--video" : " map-bubble--audio")}
                    style={bubblePosition(r)}
                    onMouseEnter={() => setHoveredRelease(r)}
                    onMouseLeave={() => setHoveredRelease(null)}
                    onClick={() => handleReleaseClick(r, i)}
                    title={`${r.artist} — ${r.song}`}
                  />
                ))}
                {hoveredRelease && (
                  <div className="map-tooltip" style={bubblePosition(hoveredRelease)}>
                    <div className="map-tooltip-title">{hoveredRelease.location}</div>
                    <div className="map-tooltip-sub">{hoveredRelease.artist} — {hoveredRelease.song}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Playback bar */}
            <div className="map-playback">
              <button onClick={() => seek(-30)} aria-label="Back 30s">«</button>
              <button onClick={() => seek(-10)} aria-label="Back 10s">‹</button>
              <button className="map-playback-main" onClick={togglePlay} aria-label={isPlaying ? "Pause" : "Play"}>
                {isPlaying ? "⏸" : "▶"}
              </button>
              <button onClick={() => seek(10)} aria-label="Forward 10s">›</button>
              <button onClick={() => seek(30)} aria-label="Forward 30s">»</button>
              <span className="map-playback-title">
                {playingId !== null && filteredReleases[playingId]
                  ? `Now playing: ${filteredReleases[playingId].artist} — ${filteredReleases[playingId].song}`
                  : "Select a track below"}
              </span>
            </div>
            <audio ref={audioRef} onEnded={() => setIsPlaying(false)} />

            {/* Content grid */}
            <div className="map-content-grid">
              {filteredReleases.map((r, i) => (
                <div
                  key={`${r.artist}-${r.song}-grid-${i}`}
                  className="map-content-item"
                  onClick={() => handleReleaseClick(r, i)}
                  role="button"
                  tabIndex={0}
                >
                  <img src={r.url} alt={`${r.artist} — ${r.song}`} />
                  <span className="map-content-icon">{r.video ? "▶" : "♪"}</span>
                  {r.runningTime && <span className="map-content-duration">{r.runningTime}</span>}
                </div>
              ))}
              {filteredReleases.length === 0 && (
                <p className="map-empty">No releases match this filter.</p>
              )}
            </div>
          </div>

          {/* Sidebar: locations */}
          <div className="map-sidebar map-sidebar--locations">
            <div className="map-sidebar-label">Locations</div>
            <button
              className={"map-filter-link" + (!selectedLocation ? " active" : "")}
              onClick={() => setSelectedLocation(null)}
            >
              All locations
            </button>
            {locations.map((loc) => (
              <button
                key={loc}
                className={"map-filter-link" + (selectedLocation === loc ? " active" : "")}
                onClick={() => setSelectedLocation(selectedLocation === loc ? null : loc)}
              >
                {loc} <span className="map-filter-count">({locationCounts[loc]})</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Video modal */}
      {videoModal && (
        <div className="map-modal-overlay" onClick={() => setVideoModal(null)}>
          <div className="map-modal" onClick={(e) => e.stopPropagation()}>
            <button className="map-modal-close" onClick={() => setVideoModal(null)}>✕</button>
            <div className="map-modal-video">
              <iframe src={videoModal.video} title={videoModal.song} allowFullScreen />
            </div>
            <div className="map-modal-info">
              <div className="map-modal-title">{videoModal.artist}: {videoModal.song}</div>
              <div className="map-modal-meta">
                <span>{videoModal.location}</span>
                <span>{videoModal.producer}</span>
                <span>{videoModal.releaseDate}</span>
                <span>{videoModal.runningTime}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
